/**
 * Header Builder API — configurable top-header.
 *
 * Backend: apps/branding/views/header_views.py (mounted at /api/v1/branding/header/).
 * Scope values: 'me' | 'company' | 'user' (user requires user_id).
 */
import apiClient from '@/api/client'

export const headerBuilderApi = {
  // Effective layout for the current user (personal -> company -> default).
  // skipErrorLog: a missing/новый эндпоинт must never spam the error journal.
  effective() {
    return apiClient.get('/branding/header/', { skipErrorLog: true })
  },

  // Raw saved layout for a scope (for the builder editor).
  getLayout(scope = 'me', userId = null) {
    return apiClient.get('/branding/header/layout/', {
      params: { scope, ...(userId ? { user_id: userId } : {}) },
    })
  },

  saveLayout(scope, layout, userId = null) {
    return apiClient.put('/branding/header/layout/', {
      scope, layout, ...(userId ? { user_id: userId } : {}),
    })
  },

  resetLayout(scope, userId = null) {
    return apiClient.delete('/branding/header/layout/', {
      params: { scope, ...(userId ? { user_id: userId } : {}) },
    })
  },

  listTemplates() {
    return apiClient.get('/branding/header/templates/')
  },

  createTemplate(name, layout, isShared = false, description = undefined) {
    const body = { name, layout, is_shared: isShared }
    if (description !== undefined) body.description = description
    return apiClient.post('/branding/header/templates/', body)
  },

  // Update an existing template in place (rename, re-save layout and/or note).
  // Used by "Save" to keep the active template in sync with the header.
  updateTemplate(id, { name = undefined, layout = undefined, description = undefined } = {}) {
    const body = {}
    if (name !== undefined) body.name = name
    if (layout !== undefined) body.layout = layout
    if (description !== undefined) body.description = description
    return apiClient.put(`/branding/header/templates/${id}/`, body)
  },

  deleteTemplate(id) {
    return apiClient.delete(`/branding/header/templates/${id}/`)
  },

  // Duplicate a template into a new "<name> (copy)". Personal copy by default;
  // pass isShared=true (needs branding.edit) to make the copy company-wide.
  duplicateTemplate(id, isShared = undefined) {
    const body = {}
    if (isShared !== undefined) body.is_shared = isShared
    return apiClient.post(`/branding/header/templates/${id}/duplicate/`, body)
  },

  applyTemplate(id, scope, userId = null) {
    return apiClient.post(`/branding/header/templates/${id}/apply/`, {
      scope, ...(userId ? { user_id: userId } : {}),
    })
  },
}

export default headerBuilderApi
